import enMessages from '@/locales/en.json'
import frMessages from '@/locales/fr.json'
import i18n from '@/plugins/i18n'

/**
 * Get nested value from object using dot notation path
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const result = path.split('.').reduce<unknown>((current, key) => {
    if (current && typeof current === 'object' && key in current) {
      return (current as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
  return result !== undefined && result !== null ? result : path
}

/**
 * Pick one form out of a vue-i18n plural message — "one | other", or
 * "zero | one | other" — using the English rules vue-i18n applies by default
 * (MYAC-1034). Exports read `en.json` straight off disk instead of going
 * through vue-i18n, so the pipe would otherwise reach the page verbatim.
 * `choice` defaults to 1, the same singular vue-i18n falls back to when a
 * plural message is translated without a count.
 */
function pickPluralForm(message: string, choice: number = 1): string {
  const forms = message.split('|')
  if (forms.length < 2) return message
  const n = Math.abs(choice)
  const index = forms.length === 2 ? (n === 1 ? 0 : 1) : n === 0 ? 0 : Math.min(n, 2)
  return (forms[index] ?? forms[forms.length - 1]).trim()
}

/** What every translator here returns — the app's own `t` shape, narrowed. */
export type PdfTranslate = (
  key: string,
  params?: Record<string, string | number>,
  plural?: number,
) => string

/**
 * Resolve one key against one bundle. Returns the KEY itself when the bundle
 * does not carry it — `getNestedValue`'s own convention, and the signal
 * `tLang` reads to fall back to English.
 */
function render(
  messages: Record<string, unknown>,
  key: string,
  params?: Record<string, string | number>,
  plural?: number,
): string {
  const value = getNestedValue(messages, key)
  let result = typeof value === 'string' ? pickPluralForm(value, plural) : String(value)

  if (params) {
    Object.keys(params).forEach((paramKey) => {
      result = result.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(params[paramKey]))
    })
  }

  return result
}

/**
 * Translation function that always uses English messages for PDF generation
 */
export function tEn(
  key: string,
  params?: Record<string, string | number>,
  plural?: number,
): string {
  return render(enMessages as Record<string, unknown>, key, params, plural)
}

/** The bundles a PDF can print from, by BASE language tag. */
const BUNDLES: Record<string, Record<string, unknown>> = {
  en: enMessages as Record<string, unknown>,
  fr: frMessages as Record<string, unknown>,
}

/**
 * Translation function bound to a GIVEN language — the language of the DATA,
 * not of the UI and not of the exporter (MYAC-776 round 8).
 *
 * Johnny read a French gap analysis whose cover said « Langue : FR » and whose
 * every status word said "To handle" / "Not concerned": the record left the app
 * half-translated, and a status word is precisely the part an auditor quotes.
 * `tEn` is right for a report whose content is English; a report whose content
 * was WRITTEN in French has to name its own facts in French.
 *
 * The tag is matched on its BASE (`fr`, `fr-FR`, `fr_CA` → `fr`), and any key
 * the bundle does not carry falls back to English rather than printing the key
 * path at a customer ("no technical keys in user-facing text"). An unknown
 * language is English, which is what the whole `utils/pdf/` family already does.
 */
export function tLang(lang: string | undefined | null): PdfTranslate {
  const tag = (lang ?? '').toLowerCase().split(/[-_]/)[0]
  const messages = BUNDLES[tag]
  if (!messages || messages === BUNDLES.en) return tEn
  return (key, params, plural) => {
    const own = render(messages, key, params, plural)
    return own === key ? tEn(key, params, plural) : own
  }
}

/**
 * Translation function that follows the CURRENT UI locale — for the handful
 * of PDF strings that must match what the user just read on screen.
 *
 * MYAC-1160 review pass (NOW-14) — the exported watch report is the audit
 * piece the RAQA hands to a notified body; it printed « Reviewed / Impactful
 * / Not applicable » under a French screen that says « Vu, sans action /
 * Impact — à traiter / Non applicable », with nothing mapping the two. The
 * REST of the PDF stays on `tEn` on purpose (the document's chrome and the
 * generated content are English today); only the review vocabulary and its
 * end legend follow the UI.
 *
 * The locale is matched on its BASE tag, not compared for equality: the app
 * runs on the raw browser tag (`fr-FR`, `en-US`) while messages are
 * registered under `fr`/`en` — see `plugins/i18n.ts`.
 */
export function tUi(
  key: string,
  params?: Record<string, string | number>,
  plural?: number,
): string {
  const raw = i18n.global.locale
  const tag = typeof raw === 'string' ? raw : raw.value
  const messages = tag.toLowerCase().startsWith('fr') ? frMessages : enMessages
  return render(messages as Record<string, unknown>, key, params, plural)
}
